<template>
  <SidebarNavigation :uri="uri">
    <div class="landing-page-editor min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
        <h1 class="text-xl sm:text-2xl font-bold text-gray-900 truncate">Landing Page Generator</h1>
        <span v-if="currentPage" class="text-xs sm:text-sm text-gray-500 truncate">
          Editing: {{ currentPage.title }}
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <span v-if="currentPage && lastSaved" class="text-xs text-gray-500 hidden sm:inline">{{ saveStatusText }}</span>
        <button
          v-if="currentPage"
          @click="copyPageLink"
          class="flex-1 sm:flex-none px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Copy Link
        </button>
        <button
          v-if="currentPage"
          @click="duplicatePage"
          :disabled="duplicating"
          class="flex-1 sm:flex-none px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Duplicate
        </button>
        <button
          v-if="currentPage"
          @click="savePage"
          :disabled="saving"
          class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
        >
          {{ saving ? 'Saving...' : 'Save Page' }}
        </button>
        <button
          v-if="currentPage"
          @click="publishPage"
          :disabled="saving"
          :class="[
            'flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md',
            currentPage.is_published
              ? 'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800'
              : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
          ]"
        >
          {{ currentPage.is_published ? 'Unpublish' : 'Publish' }}
        </button>
        <button
          v-if="currentPage"
          @click="viewPage"
          class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors shadow-sm hover:shadow-md"
        >
          View Page
        </button>
        <button
          v-if="currentPage"
          @click="deletePage"
          class="flex-1 sm:flex-none px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row h-[calc(100vh-73px)] sm:h-[calc(100vh-81px)]">
      <!-- Left Sidebar: Add Sections / Edit Section -->
      <div class="w-full lg:w-80 bg-white border-r border-gray-200 overflow-y-auto border-b lg:border-b-0">
        <div v-if="!editingSection" class="p-4 sm:p-6">
          <div v-if="currentPage" class="mb-6 pb-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <icon name="settings" class="w-5 h-5 text-green-600" />
              Page Settings
            </h2>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                <input v-model="currentPage.title" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg" @input="markDirty">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea v-model="currentPage.settings.meta_description" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="SEO description for search engines" @input="markDirty" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                <div class="flex items-center gap-2">
                  <input v-model="currentPage.settings.primary_color" type="color" class="w-12 h-10 border border-gray-300 rounded cursor-pointer" @input="markDirty">
                  <input v-model="currentPage.settings.primary_color" type="text" class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" @input="markDirty">
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  v-model="currentPage.settings.font_family"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  @change="markDirty"
                >
                  <option v-for="font in fontOptions" :key="font.value" :value="font.value">
                    {{ font.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Public URL</label>
                <p class="text-xs text-gray-500 break-all bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">{{ publicPageUrl }}</p>
              </div>
            </div>
          </div>

          <div class="mb-6 pb-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <icon name="plus" class="w-5 h-5 text-green-600" />
              Add Sections
            </h2>
            <p class="text-xs text-gray-500 mb-4">Choose a section type to add to your landing page</p>
            <div class="space-y-2">
              <button
                v-for="sectionType in sectionTypes"
                :key="sectionType.type"
                @click="addSection(sectionType.type)"
                class="w-full text-left px-4 py-3 bg-gray-50 hover:bg-green-50 border-2 border-gray-200 hover:border-green-400 rounded-lg transition-all flex items-center gap-3 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <icon :name="sectionType.icon" class="w-5 h-5 text-green-600 flex-shrink-0" />
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900">{{ sectionType.name }}</div>
                  <div class="text-xs text-gray-500 mt-0.5">{{ sectionType.description }}</div>
                </div>
                <icon name="arrow-right" class="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            </div>
          </div>

          <!-- Sections List -->
          <div v-if="currentPage && currentPage.sections && currentPage.sections.length > 0">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <icon name="category" class="w-5 h-5 text-green-600" />
                Sections
              </h3>
              <span class="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">{{ currentPage.sections.length }}</span>
            </div>
            <div class="space-y-2">
              <div
                v-for="(section, index) in currentPage.sections"
                :key="section.id || index"
                @click="editSection(section)"
                :class="[
                  'p-3 rounded-lg border-2 cursor-pointer transition-all group',
                  editingSection?.id === section.id
                    ? 'border-green-500 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-green-300 hover:bg-gray-50 hover:shadow-sm'
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 min-w-0 flex-1">
                    <span class="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">{{ index + 1 }}</span>
                    <span class="font-medium text-gray-900 truncate">{{ getSectionName(section.type) }}</span>
                  </div>
                  <button
                    @click.stop="duplicateSection(section)"
                    class="p-1.5 text-gray-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                    title="Duplicate section"
                  >
                    <icon name="plus" class="w-4 h-4" />
                  </button>
                  <button
                    @click.stop="deleteSection(section.id)"
                    class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                    title="Delete section"
                  >
                    <icon name="trash" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="currentPage" class="text-center py-8">
            <icon name="page" class="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p class="text-sm text-gray-500">No sections added yet</p>
            <p class="text-xs text-gray-400 mt-1">Add a section above to get started</p>
          </div>
        </div>

        <!-- Edit Section Mode -->
        <div v-else class="p-4 sm:p-6">
          <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <icon name="edit" class="w-5 h-5 text-green-600" />
              Edit Section
            </h2>
            <button
              @click="editingSection = null"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close editor"
            >
              <icon name="close" class="w-5 h-5" />
            </button>
          </div>

          <div class="mb-4 pb-4 border-b border-gray-100">
            <p class="text-xs text-gray-500">Section Type: <span class="font-medium text-gray-700">{{ getSectionName(editingSection.type) }}</span></p>
          </div>

          <component
            :is="getSectionEditorComponent(editingSection.type)"
            :section="editingSection"
            @update="updateSection"
          />

          <button
            type="button"
            class="mt-6 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            @click="editingSection = null"
          >
            Done Editing
          </button>
        </div>
      </div>

      <!-- Center: Live Preview -->
      <div class="flex-1 overflow-y-auto bg-gray-100 p-4 sm:p-6 lg:p-8">
        <div v-if="!currentPage" class="flex items-center justify-center h-full min-h-[400px]">
          <div class="text-center max-w-md">
            <div class="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <icon name="page" class="w-12 h-12 text-gray-400" />
            </div>
            <h3 class="text-xl sm:text-2xl font-semibold text-gray-700 mb-2">No page selected</h3>
            <p class="text-gray-500 mb-6 text-sm sm:text-base">Create a new landing page to get started building your site</p>
            <button
              @click="showCreateModal = true"
              class="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-semibold"
            >
              <icon name="plus" class="w-5 h-5 text-white fill-white inline-block mr-2" />
              Create New Page
            </button>
          </div>
        </div>

        <div v-else class="max-w-4xl mx-auto bg-white shadow-xl min-h-full w-full rounded-lg overflow-hidden" :style="previewThemeStyle">
          <!-- Page Preview -->
          <div
            v-for="(section, index) in currentPage.sections"
            :key="section.id"
            class="relative group border-b border-gray-200 last:border-b-0"
            :class="{ 'border-t-4 border-t-green-500 bg-green-50/30': editingSection && editingSection.id === section.id }"
          >
            <!-- Section Controls -->
            <div class="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                @click.stop="duplicateSection(section)"
                class="p-2 bg-white border border-gray-300 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                title="Duplicate"
              >
                <icon name="page" class="w-4 h-4" />
              </button>
              <button
                @click.stop="moveSection(index, 'up')"
                :disabled="index === 0"
                class="p-2 bg-white border border-gray-300 rounded-lg shadow-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Move Up"
              >
                <icon name="up" class="w-4 h-4" />
              </button>
              <button
                @click.stop="moveSection(index, 'down')"
                :disabled="index === currentPage.sections.length - 1"
                class="p-2 bg-white border border-gray-300 rounded-lg shadow-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Move Down"
              >
                <icon name="down" class="w-4 h-4" />
              </button>
              <button
                @click.stop="deleteSection(index)"
                class="p-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition-colors"
                title="Delete Section"
              >
                <icon name="trash" class="w-4 h-4" />
              </button>
            </div>

            <!-- Section Preview -->
            <div
              @click="editSection(section)"
              class="cursor-pointer hover:bg-gray-50/50 transition-colors"
            >
              <component
                :is="getSectionComponent(section.type)"
                :section="section"
                :preview="true"
              />
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!currentPage.sections || currentPage.sections.length === 0" class="p-12 sm:p-16 text-center">
            <div class="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <icon name="page" class="w-10 h-10 text-gray-300" />
            </div>
            <h3 class="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No sections yet</h3>
            <p class="text-gray-500 text-sm sm:text-base mb-4">Click "Add Sections" in the sidebar to get started</p>
            <button
              @click="editingSection = null"
              class="text-green-600 hover:text-green-700 font-medium text-sm flex items-center gap-1 mx-auto"
            >
              <icon name="arrow-right" class="w-4 h-4 rotate-180" />
              Go to Add Sections
            </button>
          </div>
        </div>
      </div>

      <!-- Right Sidebar: Page List -->
      <div class="w-full lg:w-64 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto p-4 sm:p-6">
        <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <icon name="page" class="w-5 h-5 text-green-600" />
            Your Pages
          </h2>
          <button
            @click="showCreateModal = true"
            class="p-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md"
            title="Create New Page"
          >
            <icon name="plus" class="w-5 h-5 text-white fill-white" />
          </button>
        </div>

        <div v-if="pagesList && pagesList.length > 0" class="space-y-2">
          <div
            v-for="page in pagesList"
            :key="page.id"
            @click="loadPage(page.id)"
            :class="[
              'p-3 rounded-lg border-2 cursor-pointer transition-all',
              currentPage && currentPage.id === page.id
                ? 'border-green-500 bg-green-50 shadow-md'
                : 'border-gray-200 hover:border-green-300 hover:bg-gray-50 hover:shadow-sm'
            ]"
          >
            <div class="font-medium text-gray-900 truncate mb-1">{{ page.title }}</div>
            <div class="text-xs text-gray-500 mb-2">{{ page.created_at }}</div>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                :class="[
                  'text-xs px-2 py-0.5 rounded font-medium',
                  page.is_published
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                ]"
              >
                {{ page.is_published ? 'Published' : 'Draft' }}
              </span>
              <span class="text-xs text-gray-500 flex items-center gap-1">
                <icon name="eye" class="w-3 h-3" />
                {{ page.views || 0 }}
              </span>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-8">
          <icon name="page" class="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p class="text-sm text-gray-500 mb-4">No pages yet</p>
          <button
            @click="showCreateModal = true"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            Create First Page
          </button>
        </div>
      </div>
    </div>

    <!-- Create Page Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="showCreateModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center gap-3 mb-2">
            <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <icon name="plus" class="w-5 h-5 text-green-600" />
            </div>
            <h3 class="text-xl font-semibold text-gray-800">Create New Landing Page</h3>
          </div>
          <p class="text-sm text-gray-500 ml-13">Give your landing page a descriptive title</p>
        </div>
        <div class="p-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
          <input
            v-model="newPageTitle"
            @keyup.enter="createPage"
            type="text"
            placeholder="e.g., Product Launch, About Us, Services"
            class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
            autofocus
          />
        </div>
        <div class="flex gap-3 justify-end p-6 pt-0 border-t border-gray-200">
          <button
            @click="showCreateModal = false"
            class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="createPage"
            :disabled="!newPageTitle || creating"
            class="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg font-semibold"
          >
            {{ creating ? 'Creating...' : 'Create Page' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import Icon from '@/Shared/Icon.vue'
import SidebarNavigation from '@/Shared/SidebarNavigation.vue'
import { saveToStorage, loadFromStorage } from '../../utils/storage.js'
import {
  SECTION_TYPES,
  DEFAULT_PAGE_SETTINGS,
  FONT_OPTIONS,
  createSection,
  getSectionComponentName,
  getSectionEditorName,
  getSectionLabel,
} from './sectionRegistry.js'
import HeroSection from './Sections/HeroSection.vue'
import HeroSectionEditor from './Sections/HeroSectionEditor.vue'
import FeaturesSection from './Sections/FeaturesSection.vue'
import FeaturesSectionEditor from './Sections/FeaturesSectionEditor.vue'
import CtaSection from './Sections/CtaSection.vue'
import CtaSectionEditor from './Sections/CtaSectionEditor.vue'
import FaqSection from './Sections/FaqSection.vue'
import FaqSectionEditor from './Sections/FaqSectionEditor.vue'
import TestimonialsSection from './Sections/TestimonialsSection.vue'
import TestimonialsSectionEditor from './Sections/TestimonialsSectionEditor.vue'

export default {
  components: {
    SidebarNavigation,
    Icon,
    HeroSection,
    HeroSectionEditor,
    FeaturesSection,
    FeaturesSectionEditor,
    CtaSection,
    CtaSectionEditor,
    FaqSection,
    FaqSectionEditor,
    TestimonialsSection,
    TestimonialsSectionEditor,
  },
  layout: Layout,
  props: {
    pages: { type: Array, default: () => [] },
    title: { type: String, default: 'Landing Page Generator' },
    uri: { type: String, default: 'landing-page' },
  },
  data() {
    return {
      pagesList: this.pages || [],
      currentPage: null,
      editingSection: null,
      saving: false,
      creating: false,
      duplicating: false,
      showCreateModal: false,
      newPageTitle: '',
      loadingPage: false,
      autoSaving: false,
      lastSaved: null,
      hasUnsavedChanges: false,
      autoSaveTimer: null,
      sectionTypes: SECTION_TYPES,
      fontOptions: FONT_OPTIONS,
    }
  },
  computed: {
    publicPageUrl() {
      if (!this.currentPage?.slug) return ''
      return `${window.location.origin}/l/${this.currentPage.slug}`
    },
    previewThemeStyle() {
      const settings = this.currentPage?.settings || DEFAULT_PAGE_SETTINGS
      return {
        '--lp-primary-color': settings.primary_color || DEFAULT_PAGE_SETTINGS.primary_color,
        fontFamily: settings.font_family || DEFAULT_PAGE_SETTINGS.font_family,
      }
    },
    saveStatusText() {
      if (this.autoSaving) return 'Auto-saving...'
      if (this.hasUnsavedChanges) return 'Unsaved changes'
      if (this.lastSaved) return `Saved ${this.lastSaved}`
      return ''
    },
  },
  mounted() {
    const cachedPages = loadFromStorage('pages_list')
    if (cachedPages?.length && !this.pagesList.length) {
      this.pagesList = cachedPages
    }
  },
  beforeUnmount() {
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
  },
  methods: {
    ensurePageSettings(page) {
      if (!page.settings) {
        page.settings = { ...DEFAULT_PAGE_SETTINGS }
      } else {
        page.settings = { ...DEFAULT_PAGE_SETTINGS, ...page.settings }
      }
      return page
    },
    getSectionName(type) {
      return getSectionLabel(type)
    },
    markDirty() {
      this.hasUnsavedChanges = true
      this.scheduleAutoSave()
      this.cacheCurrentPage()
    },
    scheduleAutoSave() {
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
      this.autoSaveTimer = setTimeout(() => {
        if (this.hasUnsavedChanges && this.currentPage) {
          this.savePage(true)
        }
      }, 30000)
    },
    cacheCurrentPage() {
      if (!this.currentPage) return
      saveToStorage(`page_${this.currentPage.id}`, { page: this.currentPage })
    },
    async loadPage(pageId) {
      this.loadingPage = true
      try {
        const cachedData = loadFromStorage(`page_${pageId}`)
        if (cachedData?.page) {
          this.currentPage = this.ensurePageSettings(cachedData.page)
          this.editingSection = null
        }

        const response = await this.axios.get(this.route('landing-pages.edit', pageId))
        if (response.data?.success) {
          this.currentPage = this.ensurePageSettings(response.data.page)
          this.editingSection = null
          this.hasUnsavedChanges = false
          saveToStorage(`page_${pageId}`, { page: this.currentPage })
        }
      } catch (error) {
        this.handleError(error, 'Failed to load page')
      } finally {
        this.loadingPage = false
      }
    },
    async createPage() {
      if (!this.newPageTitle || this.creating) return

      this.creating = true
      try {
        const response = await this.axios.post(this.route('landing-pages.store'), {
          title: this.newPageTitle,
        })

        if (response.data?.success) {
          const page = this.ensurePageSettings(response.data.page)
          this.currentPage = page
          this.pagesList.unshift({
            id: page.id,
            slug: page.slug,
            title: page.title,
            is_published: false,
            views: 0,
            created_at: page.created_at || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          })
          saveToStorage('pages_list', this.pagesList)
          this.showCreateModal = false
          this.newPageTitle = ''
          this.hasUnsavedChanges = false
        }
      } catch (error) {
        this.handleError(error, 'Failed to create page')
      } finally {
        this.creating = false
      }
    },
    async savePage(isAutoSave = false) {
      if (!this.currentPage || this.saving) return

      if (isAutoSave) this.autoSaving = true
      else this.saving = true

      try {
        const response = await this.axios.put(
          this.route('landing-pages.update', this.currentPage.id),
          {
            title: this.currentPage.title,
            sections: this.currentPage.sections,
            settings: this.currentPage.settings,
            is_published: this.currentPage.is_published,
          },
        )

        if (response.data?.success) {
          this.currentPage = this.ensurePageSettings(response.data.page)
          this.hasUnsavedChanges = false
          this.lastSaved = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          this.cacheCurrentPage()

          const listIndex = this.pagesList.findIndex((p) => p.id === this.currentPage.id)
          if (listIndex !== -1) {
            this.pagesList[listIndex].title = this.currentPage.title
            this.pagesList[listIndex].slug = this.currentPage.slug
            this.pagesList[listIndex].is_published = this.currentPage.is_published
            saveToStorage('pages_list', this.pagesList)
          }

          if (!isAutoSave && window.$toast) {
            window.$toast.success('Page saved successfully!')
          }
        }
      } catch (error) {
        if (!isAutoSave) this.handleError(error, 'Failed to save page')
      } finally {
        this.saving = false
        this.autoSaving = false
      }
    },
    async publishPage() {
      if (!this.currentPage) return
      this.currentPage.is_published = !this.currentPage.is_published
      await this.savePage()
    },
    viewPage() {
      if (!this.currentPage) return
      window.open(this.route('landing-page.view', this.currentPage.slug), '_blank')
    },
    copyPageLink() {
      if (!this.currentPage?.slug) return
      navigator.clipboard.writeText(this.publicPageUrl).then(() => {
        if (window.$toast) window.$toast.success('Page link copied!')
      })
    },
    async duplicatePage() {
      if (!this.currentPage || this.duplicating) return
      this.duplicating = true
      try {
        const response = await this.axios.post(this.route('landing-pages.duplicate', this.currentPage.id))
        if (response.data?.success) {
          const page = this.ensurePageSettings(response.data.page)
          this.pagesList.unshift({
            id: page.id,
            slug: page.slug,
            title: page.title,
            is_published: page.is_published,
            views: page.views || 0,
            created_at: page.created_at,
          })
          this.currentPage = page
          this.editingSection = null
          saveToStorage('pages_list', this.pagesList)
          if (window.$toast) window.$toast.success('Page duplicated')
        }
      } catch (error) {
        this.handleError(error, 'Failed to duplicate page')
      } finally {
        this.duplicating = false
      }
    },
    async deletePage() {
      if (!this.currentPage || !confirm('Delete this landing page permanently?')) return
      try {
        await this.axios.delete(this.route('landing-pages.destroy', this.currentPage.id))
        this.pagesList = this.pagesList.filter((p) => p.id !== this.currentPage.id)
        this.currentPage = null
        this.editingSection = null
        saveToStorage('pages_list', this.pagesList)
        if (window.$toast) window.$toast.success('Page deleted')
      } catch (error) {
        this.handleError(error, 'Failed to delete page')
      }
    },
    addSection(type) {
      if (!this.currentPage) {
        if (window.$toast) window.$toast.warning('Please create or select a page first')
        return
      }

      const section = createSection(type)
      if (!this.currentPage.sections) this.currentPage.sections = []
      this.currentPage.sections.push(section)
      this.editingSection = JSON.parse(JSON.stringify(section))
      this.markDirty()
    },
    editSection(section) {
      this.editingSection = JSON.parse(JSON.stringify(section))
    },
    updateSection(updatedSection) {
      if (!this.currentPage || !this.editingSection) return

      const index = this.currentPage.sections.findIndex((s) => s.id === updatedSection.id)
      if (index !== -1) {
        this.currentPage.sections[index] = updatedSection
        this.editingSection = JSON.parse(JSON.stringify(updatedSection))
        this.markDirty()
      }
    },
    duplicateSection(section) {
      if (!this.currentPage) return
      const copy = JSON.parse(JSON.stringify(section))
      copy.id = `section_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
      const index = this.currentPage.sections.findIndex((s) => s.id === section.id)
      this.currentPage.sections.splice(index + 1, 0, copy)
      this.markDirty()
    },
    deleteSection(indexOrId) {
      if (!this.currentPage || !confirm('Delete this section?')) return

      const editingId = this.editingSection?.id
      let index = typeof indexOrId === 'number'
        ? indexOrId
        : this.currentPage.sections.findIndex((s) => s.id === indexOrId)

      if (index === -1) return

      const removedId = this.currentPage.sections[index]?.id
      this.currentPage.sections.splice(index, 1)

      if (editingId && editingId === removedId) {
        this.editingSection = null
      }
      this.markDirty()
    },
    moveSection(index, direction) {
      if (!this.currentPage) return
      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= this.currentPage.sections.length) return

      const sections = [...this.currentPage.sections]
      ;[sections[index], sections[newIndex]] = [sections[newIndex], sections[index]]
      this.currentPage.sections = sections
      this.markDirty()
    },
    getSectionComponent(type) {
      return getSectionComponentName(type)
    },
    getSectionEditorComponent(type) {
      return getSectionEditorName(type)
    },
    handleError(error, fallback) {
      console.error(error)
      const message = error.response?.data?.message || error.message || fallback
      if (window.$toast) window.$toast.error(message)
    },
  },
}
</script>

<style scoped>
.landing-page-editor {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}
</style>

